import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle, RefreshCw, ChevronRight, CheckCircle, FileText 
} from 'lucide-react';
import {
  Card, CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ResolutionRoom } from '@/features/orders';
import { useSupplierDiscrepancies } from '@/hooks/use-discrepancies';
import type {
  Discrepancy, DiscrepancyStatus 
} from '@/types/orders';
import { DISCREPANCY_TYPE_LABELS } from '@/types/orders';

type FilterStatus = DiscrepancyStatus | 'all';

const STATUS_FILTERS: {
  value: FilterStatus;
  label: string 
}[] = [
  {
    value: 'all',
    label: 'All' 
  },
  {
    value: 'pending_supplier_response',
    label: 'Needs Response' 
  },
  {
    value: 'pending_receiver_confirmation',
    label: 'Awaiting Confirmation' 
  },
  {
    value: 'resolved',
    label: 'Resolved' 
  },
];

export function PendingActionsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState<Discrepancy | null>(null);

  const {
    data, isLoading, error, refetch 
  } = useSupplierDiscrepancies();

  const allDiscrepancies = data?.data ?? [];
  const discrepancies = statusFilter === 'all'
    ? allDiscrepancies
    : allDiscrepancies.filter((d) => d.status === statusFilter);

  const pendingCount = allDiscrepancies.filter(
    (d) => d.status === 'pending_supplier_response'
  ).length;

  const handleViewPO = (poId: string) => {
    navigate(`/orders/purchase-orders/${poId}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader pendingCount={0} />
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="h-32 animate-pulse bg-slate-100" />
          <Card className="h-32 animate-pulse bg-slate-100" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader pendingCount={0} />
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-slate-500 mb-4">Failed to load pending actions</p>
            <Button onClick={() => refetch()} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader pendingCount={pendingCount} />

      {/* Filters */}
      <div className="flex gap-2">
        {STATUS_FILTERS.map((filter) => (
          <Button
            key={filter.value}
            variant={statusFilter === filter.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(filter.value)}
            className={statusFilter === filter.value 
              ? 'bg-slate-900 hover:bg-slate-800' 
              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }
          >
            {filter.label}
            {filter.value === 'pending_supplier_response' && pendingCount > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 text-xs">
                {pendingCount}
              </span>
            )}
          </Button>
        ))}
      </div>

      {discrepancies.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <CheckCircle className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-lg font-medium text-slate-900">All clear</p>
            <p className="text-slate-500 text-sm mt-1">
              {statusFilter === 'all'
                ? 'No pending actions at the moment'
                : `No items with status "${STATUS_FILTERS.find(f => f.value === statusFilter)?.label}"`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* List */}
          <div className="space-y-3">
            {discrepancies.map((discrepancy) => (
              <DiscrepancyListItem
                key={discrepancy.discrepancyId}
                discrepancy={discrepancy}
                isSelected={selectedDiscrepancy?.discrepancyId === discrepancy.discrepancyId}
                onSelect={() => setSelectedDiscrepancy(discrepancy)}
              />
            ))}
          </div>

          {/* Resolution Room */}
          <div className="lg:sticky lg:top-6">
            {selectedDiscrepancy ? (
              <ResolutionRoom
                discrepancy={selectedDiscrepancy}
                onViewPO={handleViewPO}
              />
            ) : (
              <Card className="border-slate-200 h-[600px] flex items-center justify-center">
                <div className="text-center">
                  <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Select an item to view details</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface PageHeaderProps {readonly pendingCount: number;}

function PageHeader({ pendingCount }: PageHeaderProps) {
  return (
    <div>
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold text-slate-900">Resolution Center</h1>
        {pendingCount > 0 && (
          <span className="rounded-full bg-amber-50 text-amber-700 px-3 py-1 text-sm font-medium">
            {pendingCount} need{pendingCount === 1 ? 's' : ''} response
          </span>
        )}
      </div>
      <p className="text-slate-500 mt-1">
        Review and resolve delivery discrepancies
      </p>
    </div>
  );
}

interface DiscrepancyListItemProps {
  readonly discrepancy: Discrepancy;
  readonly isSelected: boolean;
  readonly onSelect: () => void;
}

function DiscrepancyListItem({
  discrepancy, isSelected, onSelect 
}: DiscrepancyListItemProps) {
  const needsResponse = discrepancy.status === 'pending_supplier_response';
  const isResolved = discrepancy.status === 'resolved';

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

  const getButtonClassName = () => {
    if (isSelected) {
      return 'border-slate-900 bg-slate-50 ring-1 ring-slate-900';
    }
    if (needsResponse) {
      return 'border-amber-200 bg-amber-50/30 hover:border-slate-300';
    }
    return 'border-slate-200 bg-white hover:border-slate-300';
  };

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-4 rounded-lg border transition-all ${getButtonClassName()}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-slate-900">
              {DISCREPANCY_TYPE_LABELS[discrepancy.type]}
            </span>
            {needsResponse && (
              <span className="flex items-center gap-1 text-xs text-amber-700">
                <AlertCircle className="h-3 w-3" />
                Action needed
              </span>
            )}
            {isResolved && (
              <span className="flex items-center gap-1 text-xs text-emerald-600">
                <CheckCircle className="h-3 w-3" />
                Resolved
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 truncate">
            {discrepancy.poNumber} • {discrepancy.items.length} item{discrepancy.items.length === 1 ? '' : 's'}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Reported {formatDate(discrepancy.reportedAt)} by {discrepancy.reportedByName}
          </p>
        </div>
        <ChevronRight className={`h-5 w-5 flex-shrink-0 transition-colors ${
          isSelected ? 'text-slate-900' : 'text-slate-300'
        }`} />
      </div>
    </button>
  );
}
